import { Injectable, inject } from '@angular/core';
import { AccountInfo } from '@azure/msal-browser';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { Observable, of } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { InteractionStatus, EventType, AuthenticationResult } from '@azure/msal-browser';
import { environment } from '../../../environments/environment';

/**
 * Authentication service using Azure AD with MSAL
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  /**
   * Observable of current authentication status
   */
  public readonly isAuthenticated$: Observable<boolean>;

  /**
   * Observable of interaction status (login/logout in progress)
   */
  public readonly inProgress$: Observable<boolean>;

  constructor() {
    if (environment.noAuthMode) {
      // In no-auth mode, treat as authenticated and no interactions in progress
      this.isAuthenticated$ = of(true);
      this.inProgress$ = of(false);
    } else {
      // Set up authentication status observable
      this.isAuthenticated$ = this.msalBroadcastService.msalSubject$.pipe(
        filter((msg) => msg.eventType === EventType.LOGIN_SUCCESS || 
                        msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                        msg.eventType === EventType.SSO_SILENT_SUCCESS ||
                        msg.eventType === EventType.LOGOUT_SUCCESS),
        map(() => this.isAuthenticated())
      );

      // Set up in-progress observable
      this.inProgress$ = this.msalBroadcastService.inProgress$.pipe(
        map((status: InteractionStatus) => status !== InteractionStatus.None)
      );
    }
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    if (environment.noAuthMode) {
      return true;
    }
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  /**
   * Get current user account information
   */
  getAccount(): AccountInfo | null {
    if (environment.noAuthMode) {
      return {
        homeAccountId: 'noauth.mock',
        environment: 'noauth',
        tenantId: 'noauth',
        username: 'noauth@local',
        name: 'NoAuth User',
        localAccountId: 'noauth.local',
        idTokenClaims: { roles: ['DashboardAdmin'], scp: 'Metrics.Read Metrics.Write' }
      } as unknown as AccountInfo;
    }
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      return accounts[0];
    }
    return null;
  }

  /**
   * Get user's email address
   */
  getUserEmail(): string | null {
    const account = this.getAccount();
    return account?.username || null;
  }

  /**
   * Get user's display name
   */
  getUserName(): string | null {
    const account = this.getAccount();
    return account?.name || null;
  }

  /**
   * Check if user has a specific permission/scope
   */
  hasPermission(permission: string): boolean {
    if (environment.noAuthMode) {
      return true;
    }
    const account = this.getAccount();
    if (!account) {
      return false;
    }

    // Check if user has the required scope in their ID token claims
    const idTokenClaims = account.idTokenClaims as Record<string, unknown>;
    if (!idTokenClaims) {
      return false;
    }

    // Check 'scp' claim (scopes)
    const scpClaim = idTokenClaims['scp'];
    const scopes = typeof scpClaim === 'string' ? scpClaim.split(' ') : [];
    if (scopes.includes(permission)) {
      return true;
    }

    // Check 'roles' claim
    const rolesClaim = idTokenClaims['roles'];
    const roles = Array.isArray(rolesClaim) ? rolesClaim : [];
    if (permission === 'dashboard.write' && roles.includes('DashboardAdmin')) {
      return true;
    }

    return false;
  }

  /**
   * Check if user has write permission for admin features
   */
  hasAdminAccess(): boolean {
    if (environment.noAuthMode) {
      return true;
    }
    return this.hasPermission('dashboard.write');
  }

  /**
   * Initiate login flow
   */
  login(): void {
    if (environment.noAuthMode) {
      return;
    }
    this.msalService.loginRedirect({
      scopes: environment.msal.scopes.read
    });
  }

  /**
   * Initiate logout flow
   */
  logout(): void {
    if (environment.noAuthMode) {
      return;
    }
    this.msalService.logoutRedirect({
      postLogoutRedirectUri: environment.msal.redirectUri
    });
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    if (environment.noAuthMode) {
      return environment.staticBearerToken ?? 'MOCK_TOKEN';
    }
    const account = this.getAccount();
    if (!account) {
      return null;
    }

    try {
      const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent({
        scopes: environment.msal.scopes.read,
        account: account
      });
      return result.accessToken;
    } catch (error) {
      console.error('Failed to acquire token silently', error);
      // If silent acquisition fails, trigger interactive login
      this.login();
      return null;
    }
  }
}
